import React from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '../../api/OrdersAPI';
import { BolderText } from '../BolderText/BolderText';
import { paymentStatusToAbbreviatedTranslationKey, paymentStatusToPillColor } from '../../shared/paymentStatusToTranslationKey';
import { orderStatusToTranslationKeys } from '../../shared/orderStatusToTranslationKey';
import { Icon } from '../Icon/Icon';
import { containsFileType } from '../OrderElement/OrderElement';
import CONFIG from '../../config/config';
import scaleIcon from '../../assets/images/scale.png';
import disabledScaleIcon from '../../assets/images/disabled-scale.png';
import lowPriorityIcon from '../../assets/images/warning-off.png';
import highPriorityIcon from '../../assets/images/warning.png';
import hideIcon from '../../assets/images/eye.svg';
import showIcon from '../../assets/images/eye-slash.svg';
import IconButton from '../IconButton/IconButton';
import { PermissionFlags } from '../PermissionFlags/PermissionFlags';
import Pill from '../Pill/Pill';
import Tooltip from '../Tooltip/Tooltip';

interface OrderSearchResultProps {
  order: Order;
  onView: (order: Order) => void;
  onSetPendingMeasurements?: (order: Order) => void;
  onChangePriority?: (order: Order) => void;
  onHide?: (order: Order) => void;
  canSetPendingMeasurements?: boolean;
  canPrioritize?: boolean;
}

export default function OrderSearchResult({
  order,
  onView,
  onSetPendingMeasurements,
  onChangePriority,
  onHide,
  canSetPendingMeasurements = false,
  canPrioritize = false
}: OrderSearchResultProps) {
  const { t } = useTranslation();

  const handleClick = () => {
    onView(order);
  };

  const handleSetPendingMeasurements = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSetPendingMeasurements) {
      onSetPendingMeasurements(order);
    }
  };

  const handleChangePriority = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChangePriority) {
      onChangePriority(order);
    }
  };

  const handleHide = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onHide) {
      onHide(order);
    }
  };

  return (
    <div 
      className="group p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={t("orders.search_result.view_order_aria", { clientName: order.clientName, shopName: order.shopName }) as string}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-center space-x-3 mb-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 truncate">
                {order.clientName}
              </h4>
              <p className="text-xs text-gray-600 truncate">
                {order.shopName}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Pill 
                text={t(paymentStatusToAbbreviatedTranslationKey(order.paymentStatus))} 
                color={paymentStatusToPillColor(order.paymentStatus, order.shopIdV2 === 'ATELIER')}
              />
            </div>
          </div>

          {/* Details Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-xs text-gray-600">
            <div className="flex items-center space-x-2">
              <span className="font-medium">{t("orders.search_result.product")}:</span>
              <span className="truncate">{order.dress?.name || order.dressDescription || '-'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-medium">{t("orders.search_result.delivery")}:</span>
              <BolderText text={order.deliveryDate} />
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-medium">{t("orders.search_result.invoice")}:</span>
              <span className="truncate">{order.invoiceNumber || '-'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-medium">{t("orders.search_result.status")}:</span>
              <Tooltip content={t(orderStatusToTranslationKeys(order.status))}>
                <span className="truncate">{t(orderStatusToTranslationKeys(order.status))}</span>
              </Tooltip>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-medium">{t("orders.search_result.files")}:</span>
              <div className="flex space-x-1">
                {order.notes && <span>📝</span>}
                {containsFileType(order.attachmentUrls || [], CONFIG.VIDEO_TYPES) && <span>📹</span>}
                {containsFileType(order.attachmentUrls || [], CONFIG.TEXT_TYPES.concat(CONFIG.IMAGE_TYPES)) && <span>📁</span>}
                {!order.notes && !containsFileType(order.attachmentUrls || [], CONFIG.VIDEO_TYPES) && !containsFileType(order.attachmentUrls || [], CONFIG.TEXT_TYPES.concat(CONFIG.IMAGE_TYPES)) && (
                  <span className="text-gray-400">-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end sm:justify-start space-x-1">
          {canSetPendingMeasurements && onSetPendingMeasurements && (
            <IconButton 
              actionName="Pending Measurements"
              onClick={handleSetPendingMeasurements}
              aria-label={t("orders.actions.measures")}
            >
              <Tooltip content={t("orders.actions.measures")}>
                <Icon url={order?.pendingMeasures ? scaleIcon : disabledScaleIcon} cssStyles="w-4 h-4"/>
              </Tooltip>
            </IconButton>
          )}
          {canPrioritize && onChangePriority && (
            <IconButton 
              actionName="Set High Priority"
              onClick={handleChangePriority}
              aria-label={t("orders.actions.priority")}
            >
              <Tooltip content={t("orders.actions.priority")}>
                <Icon url={order?.prioritized ? highPriorityIcon : lowPriorityIcon} cssStyles="w-4 h-4"/>
              </Tooltip>
            </IconButton>
          )}
          <PermissionFlags requiredFlags={['hide-orders']}>
            <IconButton 
              actionName="Hide/Show Order"
              onClick={handleHide}
              aria-label={order.visible ? t("orders.actions.hide") : t("orders.actions.show")}
            >
              <Tooltip content={order.visible ? t("orders.actions.hide") : t("orders.actions.show")}>
                <Icon url={order.visible ? hideIcon : showIcon} cssStyles="w-4 h-4"/>
              </Tooltip>
            </IconButton>
          </PermissionFlags>
        </div>
      </div>
    </div>
  );
}
